/**
 * Accounting Settings — beta-local configuration with sensible defaults.
 *
 * Lives in localStorage today (frontend-only beta). When Cloud lands, swap
 * the IO for a Supabase row keyed on company_id without changing callers.
 *
 * Currently exposes:
 *   • materialityFloorUsd — anomaly engine drops findings whose magnitude is
 *     below this threshold. A $250 default works for small-to-mid remodelers
 *     but commercial GCs should raise it to $1,000+ so the queue stays signal.
 */

const STORAGE_KEY = "koredor.accounting.settings.v1";

export interface AccountingSettings {
  materialityFloorUsd: number;
}

const DEFAULTS: AccountingSettings = {
  materialityFloorUsd: 250,
};

function read(): AccountingSettings {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<AccountingSettings>;
    return { ...DEFAULTS, ...parsed };
  } catch { return DEFAULTS; }
}

function write(s: AccountingSettings) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

export function getAccountingSettings(): AccountingSettings {
  return read();
}

export function getMaterialityFloor(): number {
  const v = read().materialityFloorUsd;
  return Number.isFinite(v) && v >= 0 ? v : DEFAULTS.materialityFloorUsd;
}

export function setMaterialityFloor(usd: number): AccountingSettings {
  const cur = read();
  const next = { ...cur, materialityFloorUsd: Math.max(0, Math.round(usd)) };
  write(next);
  return next;
}
